<template>
	<b-modal v-model="isActive" has-modal-card @close="closeModal()">
		<div class="modal-card modalCardContainer" style="
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translate(-50%, -50%);
			">
			<header class="modal-card-head has-darker-grey-color">
				Kinderakte exportieren{{ clientName ? " — " + clientName : "" }}
			</header>
			<section class="modal-card-body" style="padding: 1em; position: relative">
				<b-loading :is-full-page="false" v-model="isLoading"></b-loading>

				<b-field grouped>
					<b-field label="Von" expanded>
						<b-datepicker v-model="fromDate" :max-date="maxDate" placeholder="Beginn"
							icon="calendar-today" :date-formatter="formatPickerDate" trap-focus>
						</b-datepicker>
					</b-field>
					<b-field label="Bis" expanded>
						<b-datepicker v-model="toDate" :min-date="fromDate" :max-date="maxDate"
							icon="calendar-today" :date-formatter="formatPickerDate" trap-focus>
						</b-datepicker>
					</b-field>
				</b-field>
				<p class="help is-danger" v-if="hasInvalidRange">
					"Von" muss vor "Bis" liegen.
				</p>

				<hr class="sectionDivider" v-if="categories.length > 0" />

				<div v-if="categories.length > 0">
					<h4 class="category_titel mb-1">Kategorien</h4>
					<div class="is-flex is-flex-wrap-wrap">
						<b-checkbox v-model="selectedCategories" :native-value="category"
							v-for="category in categories" :key="category.id"
							class="mr-4 mb-1">
							{{ category.attributes.name }}
						</b-checkbox>
					</div>
					<p class="help">Keine Auswahl = alle Dokumente.</p>
				</div>

				<hr class="sectionDivider" />

				<div>
					<b-radio v-model="sortOrder" native-value="asc">
						Älteste zuerst
					</b-radio>
					<b-radio v-model="sortOrder" native-value="desc">
						Neueste zuerst
					</b-radio>
				</div>

				<hr class="sectionDivider" />

				<div>
					<b-checkbox v-model="includeCoverSheets"
						id="includeCoverSheetsCheckbox">
						Deckblatt vor jedem Dokument
					</b-checkbox>
				</div>
			</section>
			<footer class="modal-card-foot footerContainer">
				<b-button @click="closeModal()">
					Abbrechen
				</b-button>
				<b-button icon-left="download" type="is-primary" @click="exportPdf" :disabled="isExportDisabled"
					id="exportRecordPdfButton">
					PDF
				</b-button>
			</footer>
		</div>
	</b-modal>
</template>

<script>
/**
 * Modal for exporting the Kinderakte documents of the focused client
 * as one merged PDF. Filters: date range and document categories.
 * The documents are already in the store (the record module's
 * subscription loads all of them), so no extra query is needed.
 * @displayName RecordExportModal
 */

import { mapGetters } from "vuex";
import { printRecordPdf } from "@/lib/export/RecordPdf";
import { filterClientFilesForExport } from "@/lib/export/exportHelpers";

export default {
	name: "RecordExportModal",
	props: ["value"],
	data() {
		return {
			fromDate: undefined,
			toDate: undefined,
			selectedCategories: [],
			sortOrder: "asc",
			includeCoverSheets: false,
			isLoading: false,
			maxDate: new Date(),
		};
	},
	computed: {
		...mapGetters({
			focusedClient: "clients/getFocusedClient",
			clientRecords: "clientrecords/getFocusedClientRecords",
			categories: "clientRecordsCategories/getfocusedClientRecordCategory",
			exportedBy: "usermetas/getUserFullName",
		}),
		isActive: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			},
		},
		clientName() {
			if (!this.focusedClient) { return ""; }
			const attrs = this.focusedClient.attributes;
			return [ attrs.first_name, attrs.last_name ]
				.filter(Boolean).join(" ");
		},
		hasInvalidRange() {
			return this.fromDate && this.toDate &&
				this.fromDate > this.toDate;
		},
		isExportDisabled() {
			return this.isLoading || this.hasInvalidRange;
		},
	},
	watch: {
		value(isOpen) {
			if (isOpen) { this.initializeDefaults(); }
		},
	},
	methods: {
		initializeDefaults() {
			this.maxDate = new Date();
			this.toDate = new Date();
			// Start at the client's Hilfebeginn if known, otherwise
			// leave open (no lower bound).
			this.fromDate = this.focusedClient &&
				this.focusedClient.attributes.entry
				? new Date(this.focusedClient.attributes.entry)
				: undefined;
			this.selectedCategories = [];
			this.sortOrder = "asc";
			this.includeCoverSheets = false;
		},
		formatPickerDate(date) {
			return date.toLocaleDateString("de-DE", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
			});
		},
		async exportPdf() {
			if (!this.focusedClient) { return; }
			this.isLoading = true;
			try {
				const clientFiles = filterClientFilesForExport(
					this.clientRecords || [], {
						from: this.fromDate,
						to: this.toDate,
						categoryIds: this.selectedCategories.map((c) => c.id),
						sortOrder: this.sortOrder,
					});
				if (clientFiles.length === 0) {
					this.$buefy.toast.open({
						message: "Keine Dokumente im gewählten Zeitraum.",
						type: "is-warning",
						position: "is-bottom",
					});
					return;
				}
				await printRecordPdf({
					client: this.focusedClient,
					clientFiles,
					from: this.fromDate,
					to: this.toDate,
					categoryNames: this.selectedCategories.map(
						(category) => category.attributes.name),
					exportedBy: this.exportedBy,
					includeCoverSheets: this.includeCoverSheets,
				});
				this.isActive = false;
			}
			catch (error) {
				this.$buefy.toast.open({
					message: "Export fehlgeschlagen: " + error.toString(),
					type: "is-danger",
					position: "is-bottom",
				});
			}
			finally {
				this.isLoading = false;
			}
		},
		closeModal() {
			this.isActive = false;
		},
	},
};
</script>

<style scoped>
.modalCardContainer {
	max-height: 90vh;
	width: 34em;
	max-width: 95vw;
	box-shadow: 0 8px 40px rgba(0, 0, 0, 0.55);
	border-radius: 8px;
	overflow: hidden;
}

::v-deep .modal-background {
	background-color: rgba(10, 10, 10, 0.45);
}

/*
	b-modal renders in place (no teleport), so this modal inherits the
	white text color of the dark top bar it is mounted in. Reset to the
	regular Bulma body color for everything inside the card body.
*/
.modal-card-body {
	color: #4a4a4a;
}

::v-deep .modal-card .mdi {
	color: inherit;
}

.sectionDivider {
	margin: 0.9em 0;
	height: 1px;
	background-color: #dbdbdb;
}

.category_titel {
	font-weight: 600;
}
</style>
